import { 
    db, 
    collection, 
    query, 
    orderBy, 
    getDocs,
    addDoc,
    doc,
    deleteDoc,
    serverTimestamp,
    setDoc
} from "../../firebaseClient";
import type { GovRegAgency, GovAnnouncementStatus, ComplianceIntel } from "../types/legalOps";

export interface GovRegWatchEvent {
    id: string;
    sourceName: string;
    category: string;
    title: string;
    summary: string;
    publishedAt: string;
    url: string;
    riskLevel: string;
    status: string;
}

const COLLECTION_SOURCES = "govRegSources";
const COLLECTION_ANNOUNCEMENTS = "govRegAnnouncements";

// --- System Default Agencies (Shown even if DB has no record) ---
// Note: Super admin still has delete button; for system defaults we use a Firestore tombstone ({deleted:true})
// so they can be removed from the list without deleting this constant.
export const LOCKED_AGENCIES: GovRegAgency[] = [
  {
    id: "ftc",
    name: "公平交易委員會",
    shortName: "公平會",
    baseUrl: "https://www.ftc.gov.tw/internet/main/actProject/actProjectList.aspx?uid=127&mid=127",
    isActive: true,
    enabled: true,
    note: "【系統預設】廣告表示/競爭法遵",
  },
  {
    id: "cpc",
    name: "行政院消費者保護處",
    shortName: "消保處",
    baseUrl: "https://cpc.ey.gov.tw/Page/A3412E2A5A7B398F",
    isActive: true,
    enabled: true,
    note: "【系統預設】定型化契約/網購退換貨/消保",
  },
  {
    id: "ncc",
    name: "國家通訊傳播委員會",
    shortName: "NCC",
    baseUrl: "https://www.ncc.gov.tw/chncc/app/data/list?id=51",
    isActive: true,
    enabled: true,
    note: "【系統預設】通訊/平台治理/防詐",
  },
  {
    id: "osha",
    name: "職業安全衛生署",
    shortName: "職安署",
    baseUrl: "https://www.osha.gov.tw/48110/48417/48423/lpsimplelist",
    isActive: true,
    enabled: true,
    note: "【系統預設】職安/勞動法遵",
  },
  {
    id: "moenv",
    name: "環境部",
    shortName: "環境部",
    baseUrl: "https://docmeet.moenv.gov.tw/IFDEWebBBS_MOENV/",
    isActive: true,
    enabled: true,
    note: "【系統預設】環境/減塑/碳盤查相關",
  },
  {
    id: "boft",
    name: "經濟部國際貿易署",
    shortName: "國貿署",
    baseUrl: "https://www.trade.gov.tw/Pages/List.aspx?nodeID=39",
    isActive: true,
    enabled: true,
    note: "【系統預設】進出口/外貿法規與公告",
  },
  {
    id: "mohw",
    name: "衛生福利部",
    shortName: "衛福部",
    baseUrl: "https://www.mohw.gov.tw/lp-7186-1.html",
    isActive: true,
    enabled: true,
    note: "【系統預設】衛福部公告（入口1）",
  },
  {
    id: "mohw2",
    name: "衛生福利部",
    shortName: "衛福部2",
    baseUrl: "https://www.mohw.gov.tw/lp-7407-1.html",
    isActive: true,
    enabled: true,
    note: "【系統預設】衛福部公告（入口2）",
  },
  {
    id: "pdpc-prep",
    name: "個人資料保護委員會籌備處",
    shortName: "個資籌備處",
    baseUrl: "https://www.pdpc.gov.tw/News/20/",
    isActive: true,
    enabled: true,
    note: "【系統預設】個資/隱私/資安｜優先追蹤",
  },
  {
    id: "moda",
    name: "數位發展部",
    shortName: "數位部",
    baseUrl: "https://moda.gov.tw/press/bulletin/1179",
    isActive: true,
    enabled: true,
    note: "【系統預設】平台/資安/數位治理",
  },
  {
    id: "moea",
    name: "經濟部",
    shortName: "經濟部",
    baseUrl: "https://www.moea.gov.tw/MNS/populace/news/News.aspx?kind=2&menu_id=41",
    isActive: true,
    enabled: true,
    note: "【系統預設】商務/商品標示/產業公告",
  },
];

/**
 * 一鍵重設：把「系統預設（LOCKED）」機關的 baseUrl 恢復成程式內建的正確公告清單頁。
 * - 覆蓋 locked 的 baseUrl（保留 enabled/isActive 等自訂欄位）
 * - 不影響使用者自建來源
 * - 若 locked 曾被 tombstone（deleted:true），會順便復活
 */
export async function resetLockedAgencyBaseUrls(): Promise<void> {
  const tasks: Promise<any>[] = [];
  for (const sys of LOCKED_AGENCIES) {
    tasks.push(
      setDoc(
        doc(db, COLLECTION_SOURCES, sys.id),
        {
          name: sys.name,
          shortName: sys.shortName,
          baseUrl: sys.baseUrl,
          enabled: true,
          isActive: true,
          deleted: false,
          note: sys.note || "【系統預設】",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      )
    );
  }
  await Promise.allSettled(tasks);
}

export async function listGovAgencies(includeInactive = true): Promise<GovRegAgency[]> {
    try {
        // 1. Fetch Custom Agencies from Firestore
        let q = query(collection(db, COLLECTION_SOURCES), orderBy("createdAt", "asc"));
        const snap = await getDocs(q);
        const rawList = snap.docs.map(d => ({ id: d.id, ...d.data() } as any));

        // 1.1 Ensure system defaults exist in Firestore (so crawler/backend can also read them)
        // - If a system default doc is missing, seed it.
        // - If it exists but key fields are missing, repair it by filling from defaults.
        // - If it is tombstoned (deleted:true), do nothing.
        try {
          const byId = new Map<string, any>(rawList.map((x: any) => [String(x?.id), x]));
          const tasks: Promise<any>[] = [];

          for (const sys of LOCKED_AGENCIES) {
            const existing = byId.get(sys.id);
            if (!existing) {
              tasks.push(
                setDoc(
                  doc(db, COLLECTION_SOURCES, sys.id),
                  { ...sys, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
                  { merge: true }
                )
              );
              continue;
            }

            if (existing?.deleted === true) continue;

            const patch: any = {};
            if (!existing?.name) patch.name = sys.name;
            if (!existing?.shortName) patch.shortName = sys.shortName;
            if (!existing?.baseUrl) patch.baseUrl = sys.baseUrl;
            if (typeof existing?.enabled === "undefined") patch.enabled = sys.enabled;
            if (typeof existing?.isActive === "undefined") patch.isActive = sys.isActive;
            if (!existing?.note && sys.note) patch.note = sys.note;

            if (Object.keys(patch).length) {
              tasks.push(
                setDoc(
                  doc(db, COLLECTION_SOURCES, sys.id),
                  { ...patch, updatedAt: serverTimestamp() },
                  { merge: true }
                )
              );
            }
          }

          if (tasks.length) {
            await Promise.allSettled(tasks);
          }
        } catch (e) {
          console.warn("ensure system defaults failed", e);
        }
        const deletedIds = new Set<string>(rawList.filter((x: any) => x?.deleted === true).map((x: any) => String(x.id)));
        const nonDeleted = rawList.filter((x: any) => x?.deleted !== true);

        // 2. Merge with System Defaults (LOCKED_AGENCIES)
        // - System defaults are always present unless tombstoned (deleted:true).
        // - If a Firestore doc exists for a system default, treat it as an override (enabled/baseUrl/etc),
        //   but keep missing fields from the default so the row won't become blank.
        const sysIdSet = new Set<string>(LOCKED_AGENCIES.map(a => a.id));
        const systemList: GovRegAgency[] = LOCKED_AGENCIES
          .filter(sys => !deletedIds.has(sys.id))
          .map(sys => {
            const override = nonDeleted.find((c: any) => c?.id === sys.id);
            return override ? ({ ...sys, ...override } as GovRegAgency) : sys;
          });

        // 3. Custom (non-system) agencies remain as-is
        const customList: GovRegAgency[] = nonDeleted.filter((c: any) => !sysIdSet.has(String(c?.id))) as GovRegAgency[];

        const mergedList: GovRegAgency[] = [...systemList, ...customList];
        
        // Filter
        if (!includeInactive) {
            return mergedList.filter(a => a.isActive !== false && a.enabled !== false);
        }
        
        return mergedList;
    } catch (e) {
        console.error("listGovAgencies error", e);
        return LOCKED_AGENCIES; // Fallback
    }
}

export async function createGovAgency(input: Omit<GovRegAgency, 'createdAt' | 'updatedAt'>): Promise<void> {
    // Prevent overwriting locked IDs with create
    if (LOCKED_AGENCIES.some(l => l.id === input.id)) {
        throw new Error("此 ID 為系統保留，請直接啟用該機關。");
    }

    if (input.id) {
        await setDoc(doc(db, COLLECTION_SOURCES, input.id), {
            ...input,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
    } else {
        await addDoc(collection(db, COLLECTION_SOURCES), {
            ...input,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
    }
}

export async function updateGovAgency(id: string, input: Partial<GovRegAgency>): Promise<void> {
    // For locked agencies, we basically create/update the doc in Firestore to override defaults
    // Use set with merge to handle cases where doc doesn't exist yet
    await setDoc(doc(db, COLLECTION_SOURCES, id), {
        ...input,
        updatedAt: serverTimestamp()
    }, { merge: true });
}

export async function deleteGovAgency(id: string): Promise<void> {
    // Allow super admin UI to delete EVERY agency.
    // For system defaults, write a tombstone so it won't be injected again from LOCKED_AGENCIES.
    if (LOCKED_AGENCIES.some(l => l.id === id)) {
        await setDoc(doc(db, COLLECTION_SOURCES, id), {
            deleted: true,
            updatedAt: serverTimestamp()
        }, { merge: true });
        return;
    }
    await deleteDoc(doc(db, COLLECTION_SOURCES, id));
}

// --- Announcement Management (Legacy support) ---
// (Kept simplified for compatibility)
export async function listGovAnnouncementsByStatus(status: GovAnnouncementStatus | string): Promise<ComplianceIntel[]> {
    return []; 
}
export async function updateAnnouncementStatus(id: string, status: GovAnnouncementStatus): Promise<void> {}
export async function fetchGovAnnouncements(status = 'new'): Promise<GovRegWatchEvent[]> { return []; }
